// Infinite floating-cards marquee for the Services section.
// Cards flow continuously to the left in an endless loop, pause on hover,
// and each card gently bobs vertically to feel "alive".
//
// Trick: the track contains the services array TWICE, and the animation
// translates from 0 → -50%. When the loop restarts, the second copy is
// pixel-perfect aligned with the first, creating a seamless infinite scroll.

import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Sparkles } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";

function Card({ svc, idx, total, iconMap, t, tl, pathWithLang, ariaHidden }) {
  const Icon = iconMap[svc.icon] || Sparkles;
  return (
    <article
      data-testid={ariaHidden ? undefined : `service-${svc.key}`}
      aria-hidden={ariaHidden || undefined}
      className="svc-card themed-card flex flex-col shrink-0 service-floating-card w-[320px] sm:w-[360px] lg:w-[400px]"
      style={{ animationDelay: `${(idx % total) * 0.6}s` }}
    >
      <div
        aria-hidden="true"
        className="themed-card-backdrop"
        style={{ backgroundImage: `url(/visuals/cards/service-${svc.key}.png)` }}
      />
      <div aria-hidden="true" className="themed-card-overlay" />
      <div className="flex items-start justify-between mb-5">
        <div className="w-12 h-12 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center backdrop-blur-sm">
          <Icon size={20} className="text-[var(--gold)]" />
        </div>
        <span className="font-mono text-[11px] text-[var(--text-2)]">
          0{(idx % total) + 1} / {String(total).padStart(2, "0")}
        </span>
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2 font-medium">
        {tl(svc.tagline)}
      </div>
      <h3 className="font-display text-xl lg:text-2xl text-[var(--text-0)] leading-tight mb-3">
        {tl(svc.title)}
      </h3>
      <p className="text-[var(--text-1)] text-sm leading-relaxed mb-4 line-clamp-4">
        {tl(svc.description)}
      </p>
      <div className="space-y-1.5 mb-5">
        {svc.stack?.slice(0, 4).map((s) => (
          <div key={s} className="flex items-center gap-2 text-xs text-[var(--text-1)]">
            <Check size={12} className="text-[var(--gold)] shrink-0" />
            <span>{s}</span>
          </div>
        ))}
      </div>
      <Link
        to={pathWithLang("/kontakt")}
        tabIndex={ariaHidden ? -1 : 0}
        className="mt-auto inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--gold)] hover:text-[var(--text-0)] transition-colors"
      >
        {t("common.requestProject")} <ArrowUpRight size={12} />
      </Link>
    </article>
  );
}

export default function ServicesCarousel({ services, iconMap }) {
  const { t, tl, pathWithLang } = useI18n();
  if (!services || services.length === 0) return null;
  const total = services.length;

  return (
    <div className="relative" data-testid="services-carousel">
      {/* Edge fades */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-0 bottom-0 w-24 z-10"
        style={{
          background:
            "linear-gradient(90deg, var(--bg-0) 0%, var(--bg-0) 20%, transparent 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 bottom-0 w-24 z-10"
        style={{
          background:
            "linear-gradient(270deg, var(--bg-0) 0%, var(--bg-0) 20%, transparent 100%)",
        }}
      />

      <div className="services-marquee" data-testid="services-marquee">
        <div className="services-marquee-track">
          {/* First copy — interactive */}
          {services.map((svc, idx) => (
            <Card
              key={`a-${svc.key}`}
              svc={svc}
              idx={idx}
              total={total}
              iconMap={iconMap}
              t={t}
              tl={tl}
              pathWithLang={pathWithLang}
            />
          ))}
          {/* Second copy — clones for seamless loop, hidden from a11y */}
          {services.map((svc, idx) => (
            <Card
              key={`b-${svc.key}`}
              svc={svc}
              idx={idx}
              total={total}
              iconMap={iconMap}
              t={t}
              tl={tl}
              pathWithLang={pathWithLang}
              ariaHidden
            />
          ))}
        </div>
      </div>

      <p className="mt-6 text-xs font-mono text-[var(--text-2)] text-center">
        {t("services.marqueeHint")}
      </p>
    </div>
  );
}
